import React, { useState, useEffect, useMemo } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { Row, Col, Spin } from "antd";
import { Main, CardToolbox } from "../styled";
import { PageHeader } from "../../components/page-headers/page-headers";
import { Button } from "../../components/buttons/buttons";
import { AutoComplete } from "../../components/autoComplete/autoComplete";
import { useTranslation } from "react-i18next";
import { useCarOwnersList } from "./listDataQueries";
import {
  setAllCarOwners,
  setTableFiler,
} from "../../redux/car_owners/actionCreator";
import List from "./overview/list";
import { useDispatch, useSelector } from "react-redux";
import NewRecordModal from "./newRecordModal";
import { filtrujZoznam } from "../../utils/hladanie";
import { PanelFiltrov, moznostiZoZoznamu } from "../../components/filtre/panelFiltrov";

const CarOwnersGrid = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { path } = useRouteMatch() || {};
  const [gridState, setGridState] = useState({ addNewRecord: false });
  const [loadData, setLoadData] = useState(false);
  const [hladanie, setHladanie] = useState("");
  const PRAZDNE_FILTRE = {
    city: undefined,
    invoice_maturity: undefined,
    maFakturacnyMail: undefined,
    maRezervacnyMail: undefined,
    platcaDph: undefined,
    maBonus: undefined,
  };
  const [filtre, setFiltre] = useState(PRAZDNE_FILTRE);

  const { owners, loading: loadingList } = useCarOwnersList(loadData);
  const tableData = useSelector((state) => state?.carOwners?.allCarOwners);

  useEffect(() => {
    setLoadData(true);
  }, []);

  useEffect(() => {
    if (owners.length > 0) {
      dispatch(setAllCarOwners(owners));
    }
  }, [owners, dispatch]);

  const showNewRecordModal = () => {
    setGridState((s) => ({ ...s, addNewRecord: true }));
  };
  const onCancel = () => {
    setGridState((s) => ({ ...s, addNewRecord: false }));
  };

  // Hlada sa vo vsetkom, co je o majitelovi v zozname, nie len v nazve firmy.
  const handleSearch = (searchText) => {
    setHladanie(typeof searchText === "string" ? searchText : "");
  };

  const definicieFiltrov = useMemo(
    () => [
      {
        kluc: "city",
        popis: "Mesto",
        typ: "vyber",
        sirka: 6,
        // V datach su aj zapisy ako ".", "a", "0". Do roletky pustame len
        // hodnoty, ktore vyzeraju ako mesto (aspon tri znaky a pismeno).
        moznosti: moznostiZoZoznamu(tableData, (p) =>
          String(p?.city ?? "").trim().length >= 3 &&
          /\p{L}{2,}/u.test(String(p?.city ?? ""))
            ? p.city
            : null
        ),
      },
      {
        kluc: "invoice_maturity",
        popis: "Splatnosť faktúr",
        typ: "vyber",
        sirka: 4,
        // Dni sa triedia ako cisla, inak by islo 1, 10, 14, 2.
        moznosti: moznostiZoZoznamu(tableData, (p) =>
          p?.invoice_maturity === null || p?.invoice_maturity === undefined
            ? null
            : `${p.invoice_maturity}`
        )
          .sort((a, b) => Number(a.hodnota) - Number(b.hodnota))
          .map((m) => ({ ...m, popis: `${m.popis} dní` }))
          // Nevyplnenu splatnost ma 311 z 531 majitelov, bez tejto volby
          // sa k najvacsej skupine nedalo dostat.
          .concat([{ hodnota: "nevyplnene", popis: "nevyplnená" }]),
      },
      {
        // Bez tohto mailu nema komu odist faktura, preto sa to hodi vyfiltrovat.
        kluc: "maFakturacnyMail",
        popis: "E-mail na faktúry",
        typ: "vyber",
        sirka: 4,
        moznosti: [
          { hodnota: "ano", popis: "vyplnený" },
          { hodnota: "nie", popis: "chýba" },
        ],
      },
      {
        kluc: "maRezervacnyMail",
        popis: "E-mail na rezervácie",
        typ: "vyber",
        sirka: 4,
        moznosti: [
          { hodnota: "ano", popis: "vyplnený" },
          { hodnota: "nie", popis: "chýba" },
        ],
      },
      {
        // Prve, na co sa pozera fakturacia. Vyplnene ma 198 z 531 majitelov.
        kluc: "platcaDph",
        popis: "Platiteľ DPH",
        typ: "vyber",
        sirka: 3,
        moznosti: [
          { hodnota: "ano", popis: "áno" },
          { hodnota: "nie", popis: "nie" },
        ],
      },
      {
        kluc: "maBonus",
        popis: "Zľava nad nulu",
        typ: "vyber",
        sirka: 3,
        moznosti: [
          { hodnota: "ano", popis: "áno" },
          { hodnota: "nie", popis: "nie" },
        ],
      },
    ],
    [tableData]
  );

  const zobrazeni = useMemo(() => {
    const maHodnotu = (h) => String(h ?? "").trim() !== "";
    let data = filtrujZoznam(tableData, hladanie, (item) => [
      item?.company,
      item?.firstname,
      item?.lastname,
      item?.email,
      item?.phone,
      item?.street,
      item?.city,
      item?.zip,
      item?.ic,
      item?.dic,
      item?.ic_dph,
      item?.code,
    ]);
    if (filtre.city) {
      data = data.filter((p) => String(p?.city ?? "").trim() === filtre.city);
    }
    if (filtre.invoice_maturity === "nevyplnene") {
      data = data.filter((p) => String(p?.invoice_maturity ?? "").trim() === "");
    } else if (filtre.invoice_maturity) {
      data = data.filter(
        (p) => String(p?.invoice_maturity ?? "").trim() === filtre.invoice_maturity
      );
    }
    if (filtre.maFakturacnyMail) {
      const chcem = filtre.maFakturacnyMail === "ano";
      data = data.filter((p) => maHodnotu(p?.invoice_emails) === chcem);
    }
    if (filtre.maRezervacnyMail) {
      const chcem = filtre.maRezervacnyMail === "ano";
      data = data.filter((p) => maHodnotu(p?.reservation_emails) === chcem);
    }
    if (filtre.platcaDph) {
      const chcem = filtre.platcaDph === "ano";
      data = data.filter((p) => maHodnotu(p?.ic_dph) === chcem);
    }
    if (filtre.maBonus) {
      const chcem = filtre.maBonus === "ano";
      data = data.filter((p) => Number(p?.bonus) > 0 === chcem);
    }
    return data;
  }, [tableData, hladanie, filtre]);

  useEffect(() => {
    dispatch(setTableFiler(zobrazeni));
  }, [zobrazeni, dispatch]);

  const listPageBreadcrumb = [
    { path: "/", breadcrumbName: t("common.home") },
    { path: path, breadcrumbName: t("backend.navigation.carOwners") },
  ];

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t("carOwners.list")}
          routes={listPageBreadcrumb}
          subTitle={
            <>
              <span className="title-counter">{t("carOwners.filter")} </span>
              <AutoComplete
                onSearch={handleSearch}
                placeholder={t("carOwners.startWritting")}
                width="100%"
                patterns
              />
            </>
          }
          buttons={[
            <Button
              onClick={showNewRecordModal}
              className="btn-add_new"
              size="default"
              type="primary"
              key="1"
            >
              <Link to="#">{t("common.addRecord")}</Link>
            </Button>
          ]}
        />
      </CardToolbox>
      <Main>
        <Row gutter={15}>
          <Col md={24}>
            <PanelFiltrov
              filtre={definicieFiltrov}
              hodnoty={filtre}
              onZmena={(kluc, hodnota) =>
                setFiltre((predosle) => ({ ...predosle, [kluc]: hodnota }))
              }
              onVycistit={() => setFiltre(PRAZDNE_FILTRE)}
            />
          </Col>
        </Row>
        <Row gutter={15}>
          <Col md={24}>
            {loadingList && (
              <div className="spin">
                <Spin />
              </div>
            )}
            {/* Predtym sa pri prazdnom vysledku ukazal cely zoznam majitelov,
                takze filter s nulou najdenych vyzeral ako by nefungoval. */}
            {!loadingList && (
              <List
                inputData={zobrazeni}
                graphqlVariables={{
                  tokenGet: process.env.REACT_APP_TOKEN_GET,
                  tokenMutation: process.env.REACT_APP_TOKEN_MUTATION,
                }}
                basePath={path}
              />
            )}
          </Col>
        </Row>
      </Main>
      <NewRecordModal
        t={t}
        gridState={gridState}
        handleCancel={onCancel}
        onCancel={onCancel}
        tableData={tableData || owners}
      />
    </>
  );
};

export default CarOwnersGrid
